import dayjs from 'dayjs';
import { isEqual, zip } from 'lodash';
import {
  IDaypartRecord,
  IScheduleWeekdayDaypartRecord,
} from 'src/components/timeRow';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants';
import { HttpMethod, scheduleAssembliesTransaction } from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { IDaypartsWithDefaultAssembly } from 'src/hooks/useGetDefaultAssemblyOfDayparts';
import { IAssembly, IScheduleDaypart, IWeekdayDaypart } from 'src/interfaces';
import { uid } from 'uid';

import { AssemblyChanges } from './locationGroupTimingsGrid.interface';

export const generateWeekdayDaypartRecords = ({
  weekdayDaypartOptions,
  locGroupDayparts,
  locationGroupId,
}: {
  weekdayDaypartOptions?: IWeekdayDaypart[];
  locGroupDayparts?: IScheduleDaypart[];
  locationGroupId: number;
}): IScheduleWeekdayDaypartRecord[] | undefined => {
  if (!weekdayDaypartOptions || !locGroupDayparts) return;

  return weekdayDaypartOptions.map((weekdayDaypart) => {
    const existing = locGroupDayparts.find(
      (schedule) => schedule.weekday_daypart_id === weekdayDaypart.id,
    );

    return {
      ...(existing ? { id: existing.id } : { uid: uid() }),
      location_group_id: locationGroupId,
      weekday_daypart_id: weekdayDaypart.id,
      start_time: existing?.start_time ?? null,
      weekday: weekdayDaypart.weekday,
      daypart: weekdayDaypart.daypart,
    };
  });
};

export const getLoadingState = (
  rbStoreIntegration: boolean,
  isLoadingDayparts: boolean,
  isLoadingWeekdayDayparts: boolean,
  isLoadingLocGroupDayparts: boolean,
  isLoadingDefaultAssemblies: boolean,
) => {
  return rbStoreIntegration
    ? isLoadingDayparts || isLoadingDefaultAssemblies
    : isLoadingDayparts ||
        isLoadingWeekdayDayparts ||
        isLoadingLocGroupDayparts ||
        isLoadingDefaultAssemblies;
};

// Compare only by id
export const isSameAssembly = (a: IAssembly, b: IAssembly) => a?.id === b?.id;
// Compare only by column id
export const isSameColumnAssembly = (a: IAssembly, b: IAssembly) =>
  a?.columnId === b?.columnId;

// Generic state update helper for daypart assembly updates
export const updateDaypartScheduleAssembly = (
  setState: React.Dispatch<
    React.SetStateAction<IDaypartsWithDefaultAssembly[]>
  >,
  daypartId: number,
  updater: (defaultAssembly: IAssembly[]) => IAssembly[],
) => {
  setState((prev) => {
    const idx = prev.findIndex((dp) => dp.id === daypartId);
    if (idx === -1) return prev;

    const target = prev[idx];
    const current = target.defaultAssembly || [];

    const updatedDefaultAssembly = updater(current);

    // If nothing changed, return prev state
    if (updatedDefaultAssembly === current) return prev;

    const updated = [...prev];
    updated[idx] = {
      ...target,
      defaultAssembly: updatedDefaultAssembly,
    };

    return updated;
  });
};

export const buildScheduledAssembliesPayload = (
  daypartsRecord: IDaypartRecord[] | undefined,
  initialDaypartsRecord: IDaypartRecord[],
  locationGroupId: number,
  locationGroupName: string,
): scheduleAssembliesTransaction[] => {
  return findChangedDayparts(daypartsRecord, initialDaypartsRecord).flatMap(
    (record) =>
      createAssemblyOperations(
        record,
        initialDaypartsRecord,
        locationGroupId,
        locationGroupName,
      ),
  );
};

export const findChangedDayparts = (
  current: IDaypartRecord[] | undefined,
  initial: IDaypartRecord[],
): IDaypartRecord[] => {
  return (
    zip(current, initial)
      ?.filter(
        ([curr, init]) =>
          !isEqual(curr?.defaultAssembly, init?.defaultAssembly),
      )
      ?.map(([record]) => record as IDaypartRecord) ?? []
  );
};

export const createAssemblyOperations = (
  record: IDaypartRecord,
  initialDaypartsRecord: IDaypartRecord[],
  locationGroupId: number,
  locationGroupName: string,
): scheduleAssembliesTransaction[] => {
  const currentAssemblies = record.defaultAssembly ?? [];
  const initialAssemblies =
    initialDaypartsRecord.find((init) => init.id === record.id)
      ?.defaultAssembly ?? [];

  const changes = categorizeAssemblyChanges(
    currentAssemblies,
    initialAssemblies,
  );
  const operations: scheduleAssembliesTransaction[] = [];

  // Handle replacements
  changes.replaced.forEach((replaceAssembly) => {
    const oldAssembly = initialAssemblies.find(
      (init) => init.columnId === replaceAssembly.columnId,
    );
    if (oldAssembly) {
      operations.push(
        createUpdateOperation(
          replaceAssembly,
          oldAssembly,
          record,
          locationGroupName,
        ),
      );
    }
  });

  // Handle deletions
  changes.deleted.forEach((deleteAssembly) => {
    // Skip if this column is being reused by a new assembly
    if (!changes.new.some((na) => na.columnId === deleteAssembly.columnId)) {
      operations.push(
        createDeleteOperation(deleteAssembly, record, locationGroupName),
      );
    }
  });

  // Handle new additions
  changes.new.forEach((newAssembly) => {
    // Only create if not replacing an existing assembly in the same column
    if (!changes.replaced.some((r) => r.columnId === newAssembly.columnId)) {
      operations.push(
        createInsertOperation(
          newAssembly,
          record,
          locationGroupId,
          locationGroupName,
        ),
      );
    }
  });

  return operations;
};

export const categorizeAssemblyChanges = (
  current: IAssembly[],
  initial: IAssembly[],
): AssemblyChanges => {
  // deleted assemblies (by column or by id disappearance)
  const deletedAssemblies = initial.filter(
    (init) => !current.some((curr) => isSameColumnAssembly(curr, init)),
  );

  // re-added assemblies:
  // - existed before
  // - now present again
  // - but with reduced data (ex: lost columnId)
  const readdedWithLessData = current.filter((curr) =>
    initial.some(
      (init) =>
        init.id === curr.id && // same id
        !!init.columnId &&
        !curr.columnId, // deleted columnId (less data now)
    ),
  );

  // genuinely new assemblies that didn't exist before
  const brandNewAssemblies = current.filter(
    (curr) => !initial.some((init) => isSameAssembly(init, curr)),
  );

  // final new = brand new + re-added-with-less-data
  let newAssemblies = [
    ...brandNewAssemblies,
    ...readdedWithLessData.filter(
      (a) => !brandNewAssemblies.some((n) => n.id === a.id),
    ),
  ];

  // replacement only when "same column but changed assembly"
  const replacedAssemblies = current.filter((a) =>
    initial.some(
      (init) => !isSameAssembly(init, a) && init.columnId === a.columnId,
    ),
  );

  // remvove replacement from new assemblies if exisits as replacement will be considerd as brandNewAssemblies
  newAssemblies = newAssemblies.filter(
    (a) => !replacedAssemblies.some((r) => r.columnId === a.columnId),
  );

  return {
    new: newAssemblies,
    deleted: deletedAssemblies,
    replaced: replacedAssemblies,
  };
};

export const createUpdateOperation = (
  replaceAssembly: IAssembly,
  oldAssembly: IAssembly,
  record: IDaypartRecord,
  locationGroupName: string,
): scheduleAssembliesTransaction => {
  return {
    resource: COLLECTIONS.DEFAULT_ASSEMBLIES,
    method: HttpMethod.PUT,
    id: Number(replaceAssembly?.columnId),
    data: {
      assembly_id: replaceAssembly.id ?? null,
      start_date: dayjs().format('YYYY-MM-DD'),
      narrative: narrativeGenerators[COLLECTIONS.DEFAULT_ASSEMBLIES][
        USER_ACTIONS.UPDATE
      ](replaceAssembly.name, oldAssembly.name, record.name, locationGroupName),
    },
  };
};

export const createDeleteOperation = (
  deleteAssembly: IAssembly,
  record: IDaypartRecord,
  locationGroupName: string,
): scheduleAssembliesTransaction => {
  return {
    resource: COLLECTIONS.DEFAULT_ASSEMBLIES,
    method: HttpMethod.DELETE,
    id: Number(deleteAssembly?.columnId),
    data: {
      narrative: narrativeGenerators[COLLECTIONS.DEFAULT_ASSEMBLIES][
        USER_ACTIONS.DELETE
      ](deleteAssembly.name, record.name, locationGroupName, null, null),
    },
  };
};

export const createInsertOperation = (
  newAssembly: IAssembly,
  record: IDaypartRecord,
  locationGroupId: number,
  locationGroupName: string,
): scheduleAssembliesTransaction => {
  const startDate = newAssembly.startDate ?? dayjs();
  const isScheduledAssembly = dayjs(startDate).isAfter(dayjs(), 'day');

  const narrativeGenerator = isScheduledAssembly
    ? narrativeGenerators[COLLECTIONS.SCHEDULE_ASSEMBLIES][USER_ACTIONS.CREATE]
    : narrativeGenerators[COLLECTIONS.DEFAULT_ASSEMBLIES][USER_ACTIONS.CREATE];

  return {
    resource: COLLECTIONS.DEFAULT_ASSEMBLIES,
    method: HttpMethod.POST,
    data: {
      assembly_id: newAssembly.id ?? null,
      location_group_id: locationGroupId,
      daypart_id: record.id,
      start_date: dayjs(startDate).format('YYYY-MM-DD'),
      narrative: narrativeGenerator(
        newAssembly.name,
        record.name,
        locationGroupName,
        null,
        null,
      ),
    },
  };
};

export const updateAssemblyInPlace = (
  current: IAssembly[],
  assembly: IAssembly,
): IAssembly[] => {
  if (current.length === 0) {
    return [assembly];
  }

  const replaceableIndex = getDefaultAssembly(current);

  if (replaceableIndex === -1) {
    return current;
  }

  return current.map((a, index) =>
    index === replaceableIndex
      ? {
          ...a,
          id: assembly.id,
          name: assembly.name,
        }
      : a,
  );
};

export const getDefaultAssembly = (assemblies: IAssembly[]) => {
  return assemblies.findIndex(
    (a) =>
      !a.startDate ||
      dayjs(a.startDate).isBefore(dayjs(), 'day') ||
      dayjs(a.startDate).isSame(dayjs(), 'day'),
  );
};
